import React, { useState } from 'react';
import { FileSpreadsheet, ChevronRight, ChevronDown, Users, FileText, Receipt, DollarSign } from 'lucide-react';
import { LoadingState, fmt } from './ReportHelpers';

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border rounded p-3 flex items-center gap-3">
    <div className="w-9 h-9 rounded-md bg-orange-50 flex items-center justify-center text-[#f4931d]">
      <Icon size={18} />
    </div>
    <div>
      <p className="text-xs text-slate-500 uppercase">{label}</p>
      <p className="text-lg font-bold text-slate-800">{value}</p>
    </div>
  </div>
);

export function ReferralsTab({ data, loading, onExport }) {
  const [expanded, setExpanded] = useState({});

  if (loading) return <LoadingState />;
  if (!data) return null;

  const items = data.items || [];
  const totals = data.totals || { client_count: 0, quotes_count: 0, invoices_count: 0, revenue: 0 };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleExport = () => {
    const headers = ['Referral Source', 'Clients', 'Quotes', 'Invoices', 'Revenue'];
    const rows = items.map(r => [r.referral, r.client_count, r.quotes_count, r.invoices_count, r.revenue]);
    const summary = ['TOTAL', totals.client_count, totals.quotes_count, totals.invoices_count, totals.revenue];
    onExport('Referral Report', headers, rows, summary);
  };

  return (
    <div className="space-y-4" data-testid="referrals-tab">
      <div className="flex justify-end print:hidden">
        <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700" data-testid="export-referrals-excel">
          <FileSpreadsheet size={14} /> Export Excel
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Referral Sources" value={items.length} />
        <StatCard icon={Users} label="Clients (Businesses)" value={totals.client_count} />
        <StatCard icon={FileText} label="Quotes" value={totals.quotes_count} />
        <StatCard icon={DollarSign} label="Revenue Generated" value={fmt(totals.revenue)} />
      </div>

      {/* Referrals table with drill-down */}
      <div className="bg-white rounded-[10px] border overflow-x-auto">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Referral Source</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Clients</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Quotes</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Invoices</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No referral data for this period</td></tr>
            ) : items.map((r) => {
              const key = r.referral_id || 'no-referral';
              const isOpen = !!expanded[key];
              return (
                <React.Fragment key={key}>
                  <tr
                    className="hover:bg-orange-50/30 transition-colors cursor-pointer"
                    onClick={() => toggle(key)}
                    data-testid={`referral-row-${key}`}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap font-medium text-slate-800">
                      <span className="inline-flex items-center gap-1.5">
                        {r.clients?.length > 0
                          ? (isOpen ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />)
                          : <span className="inline-block w-[15px]" />}
                        {r.referral}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">{r.client_count}</td>
                    <td className="px-4 py-2.5 text-right">{r.quotes_count}</td>
                    <td className="px-4 py-2.5 text-right">{r.invoices_count}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{fmt(r.revenue)}</td>
                  </tr>
                  {isOpen && r.clients.map((c) => (
                    <tr key={`${key}-${c.client_id}`} className="bg-slate-50/60 text-slate-600" data-testid={`referral-client-${c.client_id}`}>
                      <td className="px-4 py-2 whitespace-nowrap pl-11">{c.name}</td>
                      <td className="px-4 py-2 text-right">—</td>
                      <td className="px-4 py-2 text-right">{c.quotes}</td>
                      <td className="px-4 py-2 text-right">{c.invoices}</td>
                      <td className="px-4 py-2 text-right">{fmt(c.revenue)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="bg-slate-800 text-white font-semibold">
                <td className="px-4 py-3 whitespace-nowrap">TOTAL</td>
                <td className="px-4 py-3 text-right">{totals.client_count}</td>
                <td className="px-4 py-3 text-right">{totals.quotes_count}</td>
                <td className="px-4 py-3 text-right">{totals.invoices_count}</td>
                <td className="px-4 py-3 text-right">{fmt(totals.revenue)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
